import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import {
  applyLocale,
  getLocale,
  persistLocale,
  t as translate,
  type Locale,
} from '@/i18n'

interface LocaleContextValue {
  locale: Locale
  setLocale: (next: Locale) => void
  t: (key: string, vars?: Record<string, string | number>) => string
}

const LocaleContext = createContext<LocaleContextValue | null>(null)

export function LocaleProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(getLocale)
  useEffect(() => {
    applyLocale(locale)
  }, [locale])

  const setLocale = useCallback((next: Locale) => {
    persistLocale(next)
    applyLocale(next)
    setLocaleState(next)
  }, [])

  const value = useMemo<LocaleContextValue>(
    () => ({
      locale,
      setLocale,
      t: (key, vars) => translate(key, vars, locale),
    }),
    [locale, setLocale],
  )

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>
}

/** Remount екранів при зміні мови — без LocaleSync, інакше акаунт відкотить перемикач. */
export function LocaleTree({ children }: { children: ReactNode }) {
  const { locale } = useLocale()
  return (
    <div key={locale} className="contents">
      {children}
    </div>
  )
}

export function useLocale(): LocaleContextValue {
  const ctx = useContext(LocaleContext)
  if (!ctx) throw new Error('useLocale outside LocaleProvider')
  return ctx
}
